import type { Ref } from 'react';
import { useCallback, useEffect, useState } from 'react';

import type { NumberFormatValues } from 'react-number-format';
import { NumericFormat } from 'react-number-format';
import { twMerge } from 'tailwind-merge';

import type { ContainerProps, RawInputProps } from '@/_components';
import { Input } from '@/_components';
import { useBoolean } from '@/_shared';

export function CellNumberInput({
  className,
  value: initialValue,
  onValueChange,
  onKeyDown,
  prefix = '',
  suffix = '',
  isAllowed,
  containerProps,
  disabled,
  commitOn = 'blur',
  inputRef,
}: RawInputProps & {
  containerProps?: ContainerProps;
  prefix?: string;
  suffix?: string;
  isAllowed?: (v: NumberFormatValues) => boolean;
  onValueChange: (v: string) => boolean;
  commitOn?: 'blur' | 'change';
  inputRef?: Ref<HTMLInputElement>;
}) {
  const [hasIssue, setHasIssue] = useBoolean(false);

  const [value, setValue] = useState(String(initialValue ?? '0'));

  const commit = useCallback(
    (val: string) => {
      if (onValueChange(val)) {
        setHasIssue.setFalse();
      } else {
        setHasIssue.setTrue();
      }
    },
    [onValueChange, setHasIssue]
  );

  const onBlur = () => {
    commit(value as string);
  };

  // If the initialValue is changed external, sync it up with our state
  useEffect(() => {
    setValue(String(initialValue ?? '0'));
  }, [initialValue]);

  const handleChange = useCallback(
    (values: NumberFormatValues) => {
      setValue(values.value ?? '0');
      if (commitOn === 'change') commit(values.value ?? '0');
    },
    [commitOn, commit]
  );

  return (
    <NumericFormat
      value={value as string}
      customInput={Input}
      getInputRef={inputRef}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      disabled={disabled}
      onValueChange={handleChange}
      containerProps={{ ...(containerProps || { className: 'h-6' }) }}
      labelProps={{ className: '' }}
      prefix={prefix}
      suffix={suffix}
      isAllowed={isAllowed}
      className={twMerge(
        'border-b-2 pt-0.5 pb-0.5 text-center text-xs',
        hasIssue && 'border-error dark:border-error',
        className
      )}
    />
  );
}
